import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ClipboardList, FileQuestion, Loader2, Plus, Search, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { AccountingSubNav } from '@/features/accounting/components/AccountingSubNav';
import { getApiErrorMessage } from '@/shared/api/axios';
import { RaiseRfqModal } from '../components/RaiseRfqModal';
import { listRfqs } from '../api/procurementRfqApi';
import { RFQ_STATUS_OPTIONS, RFQ_STATUS_TONE } from '../constants';
import { ROUTES } from '@/routes/paths';
import type { Rfq } from '../types';

/**
 * Every request for quotation, with the raise-a-new-one entry point.
 *
 * Data fetching copies the neighbouring Accounting screens (RejectionsPage,
 * ItemMasterListPage): a plain async load into local state behind a `loading`
 * flag, re-run whenever the server-side filter changes. The status filter is
 * sent to GET /api/accounting/rfq — the router filters on it — while the free
 * text box narrows the loaded page in the browser, because the list endpoint's
 * `search` is a separate round trip for what is a page-local convenience.
 *
 * No permission checks are made here. Awarding and quoting are enforced by
 * requirePermission() on the router; this screen is reached through the
 * `accounts` workspace gate on its route, the same as every other Accounting
 * page.
 */
export function RfqListPage() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState<Rfq[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [showRaise, setShowRaise] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRfqs(await listRfqs(status ? { status } : {}));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load requests for quotation.'));
      setRfqs([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rfqs.filter(rfq =>
        (rfq.rfqNumber || '').toLowerCase().includes(q) || (rfq.title || '').toLowerCase().includes(q))
    : rfqs;

  const respondedCount = (rfq: Rfq) => (rfq.quotes || []).filter(quote => quote.status === 'received').length;

  const stats = [
    { label: 'RFQs loaded', value: rfqs.length, icon: ClipboardList, color: 'indigo' as const },
    { label: 'Out for quotation', value: rfqs.filter(r => r.status === 'sent' || r.status === 'quoted').length, icon: Send, color: 'amber' as const },
    { label: 'Awarded', value: rfqs.filter(r => r.status === 'awarded').length, icon: CheckCircle2, color: 'emerald' as const },
  ];

  return (
    <div className="">
      <AccountingSubNav />
      <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
        <PageHeader
          icon={FileQuestion}
          eyebrow="Finance"
          title="Requests for Quotation"
          description={<>Ask several suppliers to quote against an approved requisition, compare the replies side by side, then award the winner into a purchase order</>}
          actions={
            <><button
                      onClick={() => setShowRaise(true)}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" /> Raise RFQ
                    </button></>
          }
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            const chip = stat.color === 'indigo'
              ? 'from-indigo-100 to-indigo-50 dark:from-indigo-950 dark:to-indigo-900/40 text-indigo-600 dark:text-indigo-400'
              : stat.color === 'amber'
              ? 'from-amber-100 to-amber-50 dark:from-amber-950 dark:to-amber-900/40 text-amber-600 dark:text-amber-400'
              : 'from-emerald-100 to-emerald-50 dark:from-emerald-950 dark:to-emerald-900/40 text-emerald-600 dark:text-emerald-400';
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.05, ease: [0.4, 0, 0.2, 1] }}
                whileHover={{ y: -2 }}
                className="rounded-xl p-4 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm flex items-start justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">{stat.label}</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-slate-100 mt-1">{stat.value}</p>
                </div>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br ${chip}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setStatus('')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              status === ''
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300'
                : 'border-slate-200 dark:border-slate-800 text-slate-500 hover:border-indigo-300'
            }`}
          >
            All
          </button>
          {RFQ_STATUS_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => setStatus(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                status === option.value
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300'
                  : 'border-slate-200 dark:border-slate-800 text-slate-500 hover:border-indigo-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
            placeholder="Search RFQ number, title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-surface-3"
        >
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <div className="h-14 w-14 rounded-xl bg-slate-100 dark:from-slate-800 dark:to-slate-900 text-slate-400 flex items-center justify-center mx-auto mb-3">
                <FileQuestion className="w-6 h-6" />
              </div>
              <p className="text-sm text-slate-400">
                {rfqs.length === 0 ? 'No requests for quotation yet.' : 'No RFQ matches that search.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>{['RFQ No', 'Title', 'Raised', 'Quotes Due', 'Items', 'Responses', 'Status', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map(rfq => (
                    <tr
                      key={rfq._id}
                      onClick={() => navigate(ROUTES.accountingV2RfqDetail.replace(':id', rfq._id))}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 cursor-pointer"
                    >
                      <td className="px-4 py-3 font-mono font-bold text-indigo-700 dark:text-indigo-400 text-xs">
                        {rfq.rfqNumber || rfq._id.slice(-8)}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{rfq.title || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {rfq.date ? new Date(rfq.date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {rfq.dueDate ? new Date(rfq.dueDate).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{(rfq.items || []).length}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {respondedCount(rfq)} of {(rfq.quotes || []).length}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`uppercase text-[10px] px-2.5 py-0.5 rounded-full font-bold ${RFQ_STATUS_TONE[rfq.status] || RFQ_STATUS_TONE.draft}`}>
                          {rfq.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <span className="text-xs font-semibold text-indigo-600">Open</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>

      <RaiseRfqModal
        open={showRaise}
        onClose={() => setShowRaise(false)}
        onCreated={rfq => {
          setShowRaise(false);
          // Straight into the new RFQ: the next thing a buyer does is send it
          // out and start recording replies, both of which live on the detail
          // screen.
          navigate(ROUTES.accountingV2RfqDetail.replace(':id', rfq._id));
        }}
      />
    </div>
  );
}

export default RfqListPage;
